import { forwardRef } from 'react'

import { cn } from '@repo/utils'

import { SimpleButton } from 'ui'
import { useMutate } from 'utils'

import type { ComponentPropsWithoutRef, MouseEventHandler } from 'react'

interface FollowButtonProps extends Omit<
  ComponentPropsWithoutRef<typeof SimpleButton>,
  'children' | 'icon' | 'primary' | 'value'
> {
  isFollowing: boolean
  tournamentId: string
}

const FollowButton = forwardRef<HTMLButtonElement, FollowButtonProps>(
  ({ className, disabled, isFollowing, onClick, tournamentId, ...props }, ref) => {
    const toggleFollowTournament = useMutate<{
      id: string
      follow: boolean
      type: 'tournaments'
    }>('search/follow', {
      invalidate: [
        ['tournaments', 'followed'],
        ['tournaments', 'admin'],
      ],
    })

    let renderAsFollowing = isFollowing
    if (toggleFollowTournament.isPending || toggleFollowTournament.isSuccess) {
      renderAsFollowing = !renderAsFollowing
    }

    const onToggleFollow: MouseEventHandler<HTMLButtonElement> = async (event) => {
      try {
        await toggleFollowTournament.mutateAsync({
          id: tournamentId,
          follow: !isFollowing,
          type: 'tournaments',
        })
      } catch (error: unknown) {
        /* empty */
      }
      onClick?.(event)
    }

    return (
      <SimpleButton
        className={cn('FollowButton', className)}
        disabled={disabled || toggleFollowTournament.isPending}
        icon={renderAsFollowing ? 'star' : 'star outline'}
        onClick={onToggleFollow}
        primary={renderAsFollowing}
        ref={ref}
        {...props}
      >
        {renderAsFollowing ? 'Siguiendo' : 'Seguir'}
      </SimpleButton>
    )
  },
)

FollowButton.displayName = 'FollowButton'

export default FollowButton
